import type {
  CloudWatchAlarmsResponse,
  CloudWatchLogGroupResponse,
  CloudWatchLogLevel,
  CloudWatchLogPageResponse,
  CloudWatchMetricsResponse,
} from 'src/api/ProactApiClient';
import {cloudWatchClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

export interface CloudWatchLogsArgs {
  group: string;
  /** ISO strings so the query arg stays serializable in the redux store. */
  start: string;
  end: string;
  text?: string;
  level: CloudWatchLogLevel;
  limit: number;
  nextToken?: string;
}

export const cloudWatchApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getCloudWatchAlarms: builder.query<CloudWatchAlarmsResponse, void>({
      queryFn: clientQuery(() => cloudWatchClient.alarms()),
      providesTags: ['CloudWatch'],
    }),
    getCloudWatchMetrics: builder.query<CloudWatchMetricsResponse, number>({
      // The API sends explicit nulls on the series where the generated types say undefined.
      queryFn: clientQuery(async (hours: number) => {
        const response = await cloudWatchClient.metrics(hours);
        return {
          ...response,
          series: (response.series ?? []).map(s => ({
            ...s,
            statistic: s.statistic ?? undefined,
            unit: s.unit ?? undefined,
            threshold: s.threshold ?? undefined,
            comparisonOperator: s.comparisonOperator ?? undefined,
            points: s.points ?? [],
          })),
        };
      }),
      providesTags: ['CloudWatch'],
    }),
    getCloudWatchLogGroups: builder.query<CloudWatchLogGroupResponse[], void>({
      queryFn: clientQuery(() => cloudWatchClient.logGroups()),
      providesTags: ['CloudWatch'],
    }),
    getCloudWatchLogs: builder.query<
      CloudWatchLogPageResponse,
      CloudWatchLogsArgs
    >({
      // The API sends an explicit null `nextToken`, which the generated type calls undefined.
      queryFn: clientQuery(async (args: CloudWatchLogsArgs) => {
        const page = await cloudWatchClient.logs(
          args.group,
          new Date(args.start),
          new Date(args.end),
          args.text,
          args.level,
          args.limit,
          args.nextToken
        );
        return {
          events: page.events ?? [],
          nextToken: page.nextToken ?? undefined,
        };
      }),
    }),
  }),
});

export const {
  useGetCloudWatchAlarmsQuery,
  useGetCloudWatchMetricsQuery,
  useGetCloudWatchLogGroupsQuery,
  useLazyGetCloudWatchLogsQuery,
} = cloudWatchApi;
